import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import DatePicker from 'react-datepicker';
import classnames from 'classnames';
import { FormGroup, FormFeedback } from '@prism/form';
import Icon from '@prism/icon';
import { Label } from '@prism/input';
import InputGroup from '@prism/inputgroup';
import { min, max, isDate, set, parse } from 'date-fns';

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function _extends() {
  _extends = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };
  return _extends.apply(this, arguments);
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};

  var target = _objectWithoutPropertiesLoose(source, excluded);

  var key, i;

  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);

    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }

  return target;
}

function _slicedToArray(arr, i) {
  return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest();
}

function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}

function _iterableToArrayLimit(arr, i) {
  var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"];

  if (_i == null) return;
  var _arr = [];
  var _n = true;
  var _d = false;

  var _s, _e;

  try {
    for (_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);

      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }

  return _arr;
}

function _unsupportedIterableToArray(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}

function _arrayLikeToArray(arr, len) {
  if (len == null || len > arr.length) len = arr.length;

  for (var i = 0, arr2 = new Array(len); i < len; i++) arr2[i] = arr[i];

  return arr2;
}

function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

var UNDEFINED = 'UNDEFINED';
var CNAME = 'time-picker';
var SIZE_SM = 'sm';
var SIZE_LG = 'lg';

/**
 * Array of strings used to parse the time string provided
 */

var formats = ['ha', 'hha', 'haaaa', 'hhaaaa', 'haaaaa', 'hhaaaaa', 'h:mma', 'hh:mma', 'h:mmaaaa', 'hh:mmaaaa', 'h:mmaaaaa', 'hh:mmaaaaa', 'h:mm a', 'hh:mm a', 'h:mm aaaa', 'hh:mm aaaa', 'h:mm aaaaa', 'hh:mm aaaaa', 'HH:mm', 'H:m', 'H:mm', 'HH:m'];
var parseTime = function parseTime(time) {
  var parsed = null;
  formats.some(function (format) {
    var d = new Date();

    try {
      parsed = parse(time, format, d);

      if (d instanceof Date && !isNaN(parsed)) {
        return true;
      }
    } catch (err) {}

    parsed = null;
    return false;
  });
  return parsed;
};
/**
 * Converts `time` to a Date object
 *
 * @param {Date | string} date
 * @returns Date
 */

var toTime = function toTime(time) {
  if (time === UNDEFINED) {
    return new Date();
  }

  if (time instanceof Date) {
    return new Date(time);
  }

  var parsed = parseTime(time);

  if (parsed) {
    return parsed;
  }

  var newTime = new Date(time);
  return newTime && !isNaN(newTime) ? newTime : new Date();
};
/**
 * Returns a new Date object from the initialTime modified by the targetTime
 *   values where config determines which values are available.
 * @param {Date} targetTime time to clone values from into the initialTime
 * @param {*} initialTime time to augment with target values
 * @param {object} config values to modify the initialTime with
 * @returns Date | null
 */

var generateTime = function generateTime(targetTime, initialTime) {
  var config = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {
    hours: true,
    minutes: true,
    seconds: true
  };

  if (!isDate(targetTime)) {
    if (!isDate(initialTime)) {
      return null;
    }

    return initialTime;
  }

  return set(toTime(initialTime), {
    hours: config.hours ? targetTime.getHours() : null,
    minutes: config.minutes ? targetTime.getMinutes() : null,
    seconds: config.seconds ? targetTime.getSeconds() : null
  });
};
/**
 * Constrain the date between the min and maximum date
 * @param {Date} date
 * @param {object} options
 * @param {Date} options.min minimum date
 * @param {Date} options.max maximum date
 * @returns
 */

var constrainTime = function constrainTime(time, _ref) {
  var _ref$min = _ref.min,
      min$1 = _ref$min === void 0 ? new Date('1/1/0100 0:0') : _ref$min,
      _ref$max = _ref.max,
      max$1 = _ref$max === void 0 ? new Date('1/1/0100 23:59:59') : _ref$max;
  var madeTime = toTime(time);
  var minTime = generateTime(toTime(min$1), madeTime);
  var maxTime = generateTime(toTime(max$1), madeTime);
  return min([max([madeTime, minTime]), maxTime]);
};

var _excluded = ["afterClose", "afterClickOutside", "className", "format", "defaultValue", "inputClassName", "invalid", "label", "maxTime", "minTime", "onChange", "placeholder", "size", "timeIntervals", "triggerClassName", "valid", "readonly"];

var TimePicker = function TimePicker(_ref) {
  var _classnames, _classnames2, _classnames3;

  var afterClose = _ref.afterClose,
      afterClickOutside = _ref.afterClickOutside,
      propClassName = _ref.className,
      format = _ref.format,
      defaultValue = _ref.defaultValue,
      propInputClassName = _ref.inputClassName,
      invalid = _ref.invalid,
      label = _ref.label,
      maxTime = _ref.maxTime,
      minTime = _ref.minTime,
      onChange = _ref.onChange,
      placeholder = _ref.placeholder,
      size = _ref.size,
      timeIntervals = _ref.timeIntervals,
      propTriggerClassName = _ref.triggerClassName,
      valid = _ref.valid,
      readonly = _ref.readonly,
      attrs = _objectWithoutProperties(_ref, _excluded);

  var timeValue = constrainTime(toTime(defaultValue), {
    min: minTime,
    max: maxTime
  });

  var _useState = useState(!!defaultValue),
      _useState2 = _slicedToArray(_useState, 2),
      showTime = _useState2[0],
      setShowTime = _useState2[1];

  var _useState3 = useState(timeValue),
      _useState4 = _slicedToArray(_useState3, 2),
      selectedTime = _useState4[0],
      setSelectedTime = _useState4[1];

  var timePickerRef = useRef(null);
  var hasMessage = typeof invalid === 'string';
  useEffect(function () {
    if (timeValue !== selectedTime) {
      setSelectedTime(timeValue);
    }
  }, [defaultValue]);

  var handleTimeChange = function handleTimeChange(time) {
    setSelectedTime(time);
    setShowTime(true);

    if (onChange) {
      onChange(time);
    }
  };

  var focusOnSelectedTime = function focusOnSelectedTime() {
    var cname = '.react-datepicker__time-list-item--selected';
    if (!requestAnimationFrame) return;
    requestAnimationFrame(function () {
      var _ref2 = timePickerRef.current && timePickerRef.current.calendar || {},
          componentNode = _ref2.componentNode;

      var domSelectied = componentNode && componentNode.querySelector(cname);

      if (domSelectied) {
        domSelectied.focus();
      } else {
        timePickerRef.current.setFocus();
      }
    });
  };

  var openTimePicker = function openTimePicker() {
    timePickerRef.current.setOpen(true);
    focusOnSelectedTime();
  };

  var _className$inputClass = {
    className: classnames((_classnames = {}, _defineProperty(_classnames, CNAME, true), _defineProperty(_classnames, "picker", true), _defineProperty(_classnames, "picker-".concat(size), size === SIZE_SM), _defineProperty(_classnames, propClassName, propClassName), _defineProperty(_classnames, 'has-message', !!hasMessage), _defineProperty(_classnames, 'is-valid', valid), _defineProperty(_classnames, 'is-invalid', invalid), _classnames)),
    inputClassName: classnames((_classnames2 = {}, _defineProperty(_classnames2, "".concat(CNAME, "-input"), true), _defineProperty(_classnames2, 'form-control', true), _defineProperty(_classnames2, "form-control-".concat(size), size === SIZE_SM), _defineProperty(_classnames2, 'inset-right', true), _defineProperty(_classnames2, propInputClassName, propInputClassName), _defineProperty(_classnames2, 'has-validation', !!hasMessage), _defineProperty(_classnames2, 'is-valid', valid), _defineProperty(_classnames2, 'is-invalid', invalid), _defineProperty(_classnames2, 'is-readonly', readonly), _classnames2)),
    triggerClassName: classnames((_classnames3 = {}, _defineProperty(_classnames3, "".concat(CNAME, "-trigger"), true), _defineProperty(_classnames3, "btn", true), _defineProperty(_classnames3, 'input-group-action', true), _defineProperty(_classnames3, "btn-".concat(size), size === SIZE_SM), _defineProperty(_classnames3, propTriggerClassName, propTriggerClassName), _classnames3))
  },
      className = _className$inputClass.className,
      inputClassName = _className$inputClass.inputClassName,
      triggerClassName = _className$inputClass.triggerClassName;
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(FormGroup, {
    className: className,
    "aria-label": "time-picker"
  }, label ? /*#__PURE__*/React.createElement(Label, {
    fontSize: size
  }, label) : null, /*#__PURE__*/React.createElement(InputGroup, null, /*#__PURE__*/React.createElement(DatePicker, _extends({}, attrs, {
    className: inputClassName,
    dateFormat: format,
    maxTime: generateTime(toTime(maxTime), selectedTime),
    minTime: generateTime(toTime(minTime), selectedTime),
    onCalendarClose: afterClose,
    onChange: handleTimeChange,
    onClickOutside: afterClickOutside,
    placeholderText: placeholder,
    ref: timePickerRef,
    selected: showTime ? selectedTime : null,
    readOnly: readonly,
    showTimeSelect: true,
    showTimeSelectOnly: true,
    timeCaption: "Time",
    timeIntervals: timeIntervals,
    wrapperClassName: "time-picker-container"
  })), !readonly && /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: triggerClassName,
    onClick: openTimePicker,
    disabled: attrs.disabled
  }, /*#__PURE__*/React.createElement(Icon, {
    glyph: "clock"
  })), !readonly && invalid && hasMessage ? /*#__PURE__*/React.createElement(FormFeedback, null, /*#__PURE__*/React.createElement(Icon, {
    glyph: "notification-circle",
    className: "small mr-1"
  }), invalid) : null)));
};

TimePicker.propTypes = {
  afterClickOutside: PropTypes.func,
  afterClose: PropTypes.func,
  className: PropTypes.string,
  format: PropTypes.string,
  defaultValue: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
  disabled: PropTypes.bool,
  inputClassName: PropTypes.string,
  invalid: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  label: PropTypes.string,
  maxTime: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
  minTime: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  size: PropTypes.oneOf([SIZE_SM, SIZE_LG]),
  timeIntervals: PropTypes.number,
  triggerClassName: PropTypes.string,
  valid: PropTypes.bool,
  readonly: PropTypes.bool
};
TimePicker.defaultProps = {
  afterClose: function afterClose() {},
  className: '',
  inputClassName: '',
  label: '',
  onChange: function onChange() {},
  triggerClassName: '',
  afterClickOutside: function afterClickOutside() {},
  format: 'h:mm aaa',
  defaultValue: UNDEFINED,
  disabled: false,
  invalid: false,
  maxTime: new Date('1/1/0100 23:59:59'),
  minTime: new Date('1/1/0100 00:00:00'),
  placeholder: 'hh:mm',
  timeIntervals: 15,
  size: SIZE_LG,
  valid: false,
  readonly: false
};
TimePicker.displayName = 'TimePicker';

export { TimePicker as default };
//# sourceMappingURL=index.js.map
